import React, { useState, memo } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import Icon from "../AppIcon";
import { useAuth } from "../../contexts/AuthContext";
import { teacherQueries } from "../../api/queries/teacher";
import { logger } from "../../utils/logger";

interface TeacherProfile {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
}

interface TeacherHeaderProps {
  onMenuClick?: () => void;
}

const TeacherHeader: React.FC<TeacherHeaderProps> = memo(({ onMenuClick }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const { data: teacherProfile, isLoading: profileLoading } = useQuery({
    ...teacherQueries.profile(),
    enabled: !!user && user.role === "TEACHER",
  });

  const handleLogout = async () => {
    logger.debug("TeacherHeader: Logout button clicked");
    try {
      await logout();
      logger.debug("TeacherHeader: Logout successful, navigating to login");
      navigate("/auth/login", { replace: true });
    } catch (error) {
      // Even if logout fails, redirect to login
      console.warn("TeacherHeader: Logout error:", error);
      navigate("/auth/login", { replace: true });
    }
  };

  const displayName = teacherProfile?.firstName && teacherProfile?.lastName 
    ? `${teacherProfile.firstName} ${teacherProfile.lastName}` 
    : user?.email || "Teacher";
  const displayEmail = teacherProfile?.email || user?.email || "";
  const displayUsername = teacherProfile?.username || user?.email || "";

  return (
    <header className="sticky top-0 z-1100 h-16 border-b border-border bg-card">
      <div className="flex h-full items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden text-muted-foreground hover:text-foreground transition-smooth"
            aria-label="Toggle menu"
          >
            <Icon name="Menu" size={24} />
          </button>

          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
              <Icon name="GraduationCap" size={24} color="white" />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-semibold text-foreground">
                EduTech
              </span>
              <span className="text-xs text-muted-foreground">
                Teacher Portal
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="h-6 w-px bg-border" />

          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-2 rounded-lg p-2 hover:bg-muted transition-smooth"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                {profileLoading
                  ? "..."
                  : teacherProfile?.firstName?.charAt(0).toUpperCase() || "T"}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-foreground">
                  {displayName}
                </p>
                <p className="text-xs text-muted-foreground">{displayEmail}</p>
                <p className="text-xs text-muted-foreground">@{displayUsername}</p>
              </div>
              <Icon
                name="ChevronDown"
                size={16}
                className="hidden md:block text-muted-foreground"
              />
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg py-1 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-all duration-200 font-medium"
                >
                  <Icon name="LogOut" size={16} className="text-red-600" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
});

TeacherHeader.displayName = "TeacherHeader";

export default TeacherHeader;
